/**
 * useAppointmentSync Hook
 * 
 * Handles syncing appointments to native calendar AND scheduling push notification reminders.
 * This is the single entry point for all appointment calendar/notification operations.
 * 
 * Features:
 * - Add appointments to native device calendar
 * - Schedule local push notification reminders
 * - Update/cancel synced events and reminders
 * - Track sync status per appointment
 * - Graceful web fallbacks
 */

import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import nativeCalendarService, {
  createNativeEvent,
  deleteNativeEvent,
  isNativeCalendarAvailable,
  requestCalendarPermission,
  exportEventAsICS,
} from '@/services/nativeCalendarService';
import nativeNotificationService, {
  scheduleAppointmentReminders,
  cancelAppointmentReminders,
  requestNotificationPermission,
  isNativeNotificationAvailable,
} from '@/services/nativeNotificationService';
import API from '@/utils/api';

// Default reminder times (in minutes before appointment)
const DEFAULT_REMINDER_TIMES = [
  1440, // 1 day before
  120,  // 2 hours before
  30,   // 30 minutes before
];

/**
 * Hook for appointment calendar and notification sync
 */
const useAppointmentSync = () => {
  const [syncing, setSyncing] = useState(null); // appointment ID currently syncing
  const [syncStatus, setSyncStatus] = useState({}); // { appointmentId: { calendar: bool, notifications: bool } }

  /**
   * Add appointment to calendar AND schedule reminders
   */
  const syncAppointment = useCallback(async (appointment, options = {}) => {
    const {
      addToCalendar = true,
      scheduleReminders = true,
      reminderTimes = DEFAULT_REMINDER_TIMES,
      showToasts = true,
    } = options;

    const {
      id,
      doctorName,
      specialty,
      appointmentDate,
      appointmentTime,
      reason,
      native_event_id,
      reminder_notification_ids,
    } = appointment;

    if (!id || !appointmentDate) {
      return { success: false, error: 'Invalid appointment data' };
    }

    setSyncing(id);
    
    const results = {
      success: true,
      calendar: null,
      notifications: null,
    };

    try {
      // Build event data
      const startDate = new Date(appointmentDate);
      if (appointmentTime) {
        const [hours, minutes] = appointmentTime.split(':');
        startDate.setHours(parseInt(hours, 10), parseInt(minutes, 10));
      }

      const endDate = new Date(startDate);
      endDate.setHours(endDate.getHours() + 1); // Default 1 hour duration

      const eventData = {
        title: `${doctorName || 'Doctor'} Appointment`,
        description: [
          reason ? `Reason: ${reason}` : '',
          specialty ? `Specialty: ${specialty}` : '',
          'Scheduled via Forget Me Not',
        ].filter(Boolean).join('\n'),
        location: specialty || '',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        allDay: false,
        reminder: 60, // 1 hour reminder in calendar
      };

      // ===== CALENDAR SYNC =====
      if (addToCalendar) {
        const isCalendarAvailable = isNativeCalendarAvailable();
        
        if (isCalendarAvailable) {
          // Skip if already synced
          if (native_event_id) {
            if (showToasts) {
              toast.info('Already in your calendar', { duration: 2000 });
            }
            results.calendar = { success: true, alreadySynced: true, eventId: native_event_id };
          } else {
            // Request permission and create event
            const { granted } = await requestCalendarPermission();
            
            if (granted) {
              const calResult = await createNativeEvent(eventData);
              
              if (calResult.success && calResult.eventId) {
                // Save event ID to backend
                try {
                  await API.put(`/shared-medical/appointment-preps/${id}`, {
                    native_event_id: calResult.eventId,
                  });
                } catch (e) {
                  console.warn('Failed to save event ID to backend:', e);
                }
                
                results.calendar = calResult;
                
                if (showToasts) {
                  toast.success('Added to calendar', {
                    description: `${eventData.title} on ${startDate.toLocaleDateString()}`,
                  });
                }
              } else {
                results.calendar = calResult;
              }
            } else {
              results.calendar = { success: false, error: 'Calendar permission denied' };
            }
          }
        } else {
          // Web fallback - offer ICS download
          if (showToasts) {
            toast.info('Calendar sync requires the mobile app', {
              description: 'Downloading calendar file instead...',
              duration: 3000,
            });
          }
          
          exportEventAsICS({
            title: eventData.title,
            date: appointmentDate,
            time: appointmentTime,
            description: eventData.description,
            notes: eventData.description,
          });
          
          results.calendar = { success: true, webFallback: true };
        }
      }

      // ===== NOTIFICATION REMINDERS =====
      if (scheduleReminders) {
        const isNotificationAvailable = isNativeNotificationAvailable();
        
        if (isNotificationAvailable || 'Notification' in window) {
          // Cancel existing reminders first
          if (reminder_notification_ids?.length) {
            await cancelAppointmentReminders(id);
          }
          
          const { granted } = await requestNotificationPermission();
          
          if (granted) {
            const notifResults = await scheduleAppointmentReminders(
              { ...appointment, doctorName, appointmentDate, appointmentTime },
              reminderTimes
            );
            
            // Get successful notification IDs
            const successfulIds = notifResults
              .filter(r => r.success)
              .map(r => r.notificationId);
            
            if (successfulIds.length > 0) {
              // Save notification IDs to backend
              try {
                await API.put(`/shared-medical/appointment-preps/${id}`, {
                  reminder_notification_ids: successfulIds,
                  reminder_times: reminderTimes,
                });
              } catch (e) {
                console.warn('Failed to save notification IDs to backend:', e);
              }
              
              results.notifications = {
                success: true,
                scheduled: successfulIds.length,
                reminderTimes,
              };
              
              if (showToasts) {
                toast.success('Reminders scheduled', {
                  description: `You'll be notified ${formatReminderTimes(reminderTimes)}`,
                });
              }
            } else {
              results.notifications = {
                success: false,
                error: 'No reminders could be scheduled',
              };
            }
          } else {
            results.notifications = { success: false, error: 'Notification permission denied' };
            
            if (showToasts) {
              toast.warning('Notification permission denied', {
                description: 'Enable notifications in settings to receive reminders',
              });
            }
          }
        } else {
          results.notifications = { success: false, error: 'Notifications not supported' };
        }
      }

      // Update sync status
      setSyncStatus(prev => ({
        ...prev,
        [id]: {
          calendar: results.calendar?.success || false,
          notifications: results.notifications?.success || false,
        },
      }));

      results.success = (results.calendar?.success !== false) || (results.notifications?.success !== false);
      
    } catch (error) {
      console.error('Appointment sync error:', error);
      results.success = false;
      results.error = error.message;
      
      if (showToasts) {
        toast.error('Failed to sync appointment');
      }
    } finally {
      setSyncing(null);
    }

    return results;
  }, []);

  /**
   * Remove appointment from calendar and cancel reminders
   */
  const unsyncAppointment = useCallback(async (appointment, options = {}) => {
    const { showToasts = true } = options;
    const { id, native_event_id, reminder_notification_ids } = appointment;

    setSyncing(id);

    try {
      // Remove from calendar
      if (native_event_id) {
        await deleteNativeEvent(native_event_id);
        await API.put(`/shared-medical/appointment-preps/${id}`, {
          native_event_id: null,
        });
      }

      // Cancel notifications
      await cancelAppointmentReminders(id);
      await API.put(`/shared-medical/appointment-preps/${id}`, {
        reminder_notification_ids: [],
        reminder_times: [],
      });

      setSyncStatus(prev => ({
        ...prev,
        [id]: { calendar: false, notifications: false },
      }));

      if (showToasts) {
        toast.success('Removed from calendar and reminders cancelled');
      }

      return { success: true };
    } catch (error) {
      console.error('Unsync error:', error);
      if (showToasts) {
        toast.error('Failed to remove appointment sync');
      }
      return { success: false, error: error.message };
    } finally {
      setSyncing(null);
    }
  }, []);

  /**
   * Quick add to calendar only (no reminders)
   */
  const addToCalendarOnly = useCallback(async (appointment) => {
    return syncAppointment(appointment, {
      addToCalendar: true,
      scheduleReminders: false,
    });
  }, [syncAppointment]);

  /**
   * Schedule reminders only (no calendar)
   */
  const scheduleRemindersOnly = useCallback(async (appointment, reminderTimes) => {
    return syncAppointment(appointment, {
      addToCalendar: false,
      scheduleReminders: true,
      reminderTimes: reminderTimes || DEFAULT_REMINDER_TIMES,
    });
  }, [syncAppointment]);

  /**
   * Check if appointment is synced
   */
  const isSynced = useCallback((appointment) => {
    return !!(appointment?.native_event_id || appointment?.reminder_notification_ids?.length);
  }, []);

  return {
    syncAppointment,
    unsyncAppointment,
    addToCalendarOnly,
    scheduleRemindersOnly,
    isSynced,
    syncing,
    syncStatus,
  };
};

/**
 * Format reminder times for display
 */
const formatReminderTimes = (times) => {
  const labels = times.map(mins => {
    if (mins >= 1440) {
      const days = Math.round(mins / 1440);
      return `${days} day${days > 1 ? 's' : ''} before`;
    } else if (mins >= 60) {
      const hours = Math.round(mins / 60);
      return `${hours} hour${hours > 1 ? 's' : ''} before`;
    } else {
      return `${mins} min before`;
    }
  });
  
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return labels.join(' and ');
  return labels.slice(0, -1).join(', ') + ', and ' + labels[labels.length - 1];
};

export default useAppointmentSync;
